<script setup lang="ts">
import { computed, useTemplateRef, watch } from 'vue';
import { useTippy } from 'vue-tippy';
import { $t } from '@teachino/shared-i18n';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import { AI_ASSISTANT_Z_INDEX } from '../layering/layering';
import {
  ASSISTANT_BUTTON_ICON_SIZE_LG_PX,
  ASSISTANT_BUTTON_ICON_SIZE_MD_PX,
  ASSISTANT_BUTTON_SIZE_LG_PX,
  ASSISTANT_BUTTON_SIZE_MD_PX,
  type AssistantButtonProps,
} from './assistant-button';

const props = withDefaults(defineProps<AssistantButtonProps>(), {
  size: 'md',
  variant: 'light',
});

const emits = defineEmits<{
  click: [event: MouseEvent];
}>();

const openAssistantLabel = computed(() => $t('ai.assistant.launcher.tooltip'));
const buttonRef = useTemplateRef<HTMLButtonElement>('buttonRef');

const buttonSizePx = computed(() =>
  props.size === 'lg'
    ? ASSISTANT_BUTTON_SIZE_LG_PX
    : ASSISTANT_BUTTON_SIZE_MD_PX,
);

const iconSizePx = computed(() =>
  props.size === 'lg'
    ? ASSISTANT_BUTTON_ICON_SIZE_LG_PX
    : ASSISTANT_BUTTON_ICON_SIZE_MD_PX,
);

const variantClass = computed(() =>
  props.variant === 'default'
    ? 'assistant-button--default'
    : 'assistant-button--light',
);

const tooltipContent = computed(
  () => props.tooltip ?? openAssistantLabel.value,
);

watch(
  buttonRef,
  (el) => {
    if (!el) return;
    useTippy(el, {
      content: tooltipContent.value,
      placement: 'top',
      arrow: false,
      maxWidth: 192,
      theme: 'teachino-tippy',
      duration: 0,
      trigger: 'mouseenter focus',
      touch: false,
      appendTo: () => document.body,
    });
  },
  { once: true },
);

const handleClick = (event: MouseEvent): void => {
  emits('click', event);
};
</script>

<template>
  <button
    ref="buttonRef"
    type="button"
    class="assistant-button touch-manipulation disabled:cursor-not-allowed disabled:opacity-50"
    :class="[variantClass, props.floating ? 'assistant-button--floating' : '']"
    :style="{
      width: `${String(buttonSizePx)}px`,
      height: `${String(buttonSizePx)}px`,
      ...(props.floating ? { zIndex: AI_ASSISTANT_Z_INDEX } : {}),
    }"
    :disabled="props.disabled"
    :aria-label="openAssistantLabel"
    @click="handleClick"
  >
    <svg
      class="assistant-button-gradient-defs"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient
          id="assistant-button-icon-gradient"
          x1="0"
          y1="0"
          x2="1"
          y2="1"
        >
          <stop offset="0" stop-color="var(--color-secondary-600)" />
          <stop offset="1" stop-color="var(--color-primary-500)" />
        </linearGradient>
      </defs>
    </svg>
    <HugeIcon
      icon="sparkle"
      :size="iconSizePx"
      class="assistant-button__icon"
    />
  </button>
</template>

<style scoped>
.assistant-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  transition:
    transform 200ms ease,
    box-shadow 200ms ease,
    background-image 200ms ease,
    border-color 200ms ease,
    color 200ms ease;
}

.assistant-button--default {
  color: var(--color-primary-500);
  border: 1px solid transparent;
  background:
    linear-gradient(135deg, var(--color-secondary-50), var(--color-primary-50))
      padding-box,
    linear-gradient(
        135deg,
        var(--color-secondary-500),
        var(--color-primary-400)
      )
      border-box;
  box-shadow:
    -0.125rem 0.125rem 0.5rem
      color-mix(in srgb, var(--color-secondary-600) 16%, transparent),
    0.125rem -0.125rem 0.5rem
      color-mix(in srgb, var(--color-primary-500) 16%, transparent);
}

.assistant-button--default:not(:disabled):hover {
  transform: scale(1.05);
  box-shadow:
    -0.125rem 0.125rem 0.75rem
      color-mix(in srgb, var(--color-secondary-600) 24%, transparent),
    0.125rem -0.125rem 0.75rem
      color-mix(in srgb, var(--color-primary-500) 24%, transparent);
}

.assistant-button--default:not(:disabled):active {
  transform: scale(0.97);
  box-shadow:
    -0.125rem 0.125rem 0.375rem
      color-mix(in srgb, var(--color-secondary-600) 16%, transparent),
    0.125rem -0.125rem 0.375rem
      color-mix(in srgb, var(--color-primary-500) 16%, transparent);
}

.assistant-button-gradient-defs {
  position: absolute;
  width: 0;
  height: 0;
}

.assistant-button--default :deep(.assistant-button__icon path) {
  stroke: url('#assistant-button-icon-gradient');
}

.assistant-button--light {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  color: var(--color-primary-600);
  border: 1px solid var(--color-primary-200);
  background-color: var(--color-primary-50);
  background-image: linear-gradient(
    114deg,
    var(--color-secondary-100) 0%,
    var(--color-primary-50) 52%,
    var(--color-primary-100) 100%
  );
  transition:
    box-shadow 200ms ease,
    background-image 200ms ease,
    border-color 200ms ease,
    color 200ms ease;
  box-shadow:
    var(--shadow-item-form),
    var(--shadow-2),
    0 0 32px -6px color-mix(in srgb, var(--color-primary-500) 42%, transparent),
    0 0 0 1px rgb(255 255 255 / 0.7) inset;
}

.assistant-button--light::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  background-image: linear-gradient(
    114deg,
    transparent 0%,
    transparent 34%,
    color-mix(in srgb, var(--color-secondary-200) 75%, transparent) 44%,
    color-mix(in srgb, var(--color-primary-200) 80%, transparent) 50%,
    color-mix(in srgb, var(--color-secondary-200) 75%, transparent) 56%,
    transparent 66%,
    transparent 100%
  );
  background-size: 300% 100%;
  background-position: -40% 50%;
}

.assistant-button--light > * {
  position: relative;
  z-index: 1;
}

.assistant-button--light:not(:disabled):hover {
  animation: none;
  transform: scale(1.05);
  color: var(--color-primary-700);
  border-color: var(--color-primary-300);
  background-image: linear-gradient(
    114deg,
    var(--color-secondary-200) 0%,
    var(--color-primary-100) 52%,
    var(--color-primary-200) 100%
  );
  box-shadow:
    0 0 16px rgb(0 0 0 / 0.22),
    0 12px 36px rgb(100 100 111 / 0.28),
    0 0 40px -4px color-mix(in srgb, var(--color-primary-500) 50%, transparent),
    0 0 0 1px rgb(255 255 255 / 0.8) inset;
}

.assistant-button--light:not(:disabled):hover::before,
.assistant-button--light:not(:disabled):active::before {
  opacity: 0;
  animation: none;
}

.assistant-button--light:not(:disabled):active {
  animation: none;
  transform: scale(0.97);
  box-shadow:
    var(--shadow-10),
    0 0 24px -8px color-mix(in srgb, var(--color-primary-500) 36%, transparent),
    0 0 0 1px rgb(255 255 255 / 0.65) inset;
}

@media (prefers-reduced-motion: no-preference) {
  .assistant-button--light:not(:disabled) {
    animation: assistant-button-scale-attention 5s linear infinite;
  }

  .assistant-button--light:not(:disabled)::before {
    animation: assistant-button-gradient-attention 5s linear infinite;
  }
}

@keyframes assistant-button-scale-attention {
  0%,
  70%,
  100% {
    transform: scale(1);
  }

  71%,
  77.999% {
    transform: scale(1);
  }

  78% {
    transform: scale(1);
  }

  83% {
    transform: scale(1.08);
  }

  88%,
  99.999% {
    transform: scale(1);
  }
}

@keyframes assistant-button-gradient-attention {
  0%,
  70%,
  100% {
    opacity: 0;
    background-position: -40% 50%;
  }

  71%,
  77.999% {
    opacity: 1;
    background-position: -40% 50%;
  }

  78% {
    opacity: 1;
    background-position: -40% 50%;
  }

  90% {
    opacity: 1;
    background-position: 140% 50%;
  }

  91%,
  99.999% {
    opacity: 0;
    background-position: 140% 50%;
  }
}

.assistant-button--floating {
  position: fixed;
  right: 24px;
  bottom: 24px;
  pointer-events: auto;
}

@media (max-width: 639px) {
  .assistant-button--floating {
    bottom: calc(4rem + env(safe-area-inset-bottom, 0px) + 16px);
  }
}
</style>
